/* LAV TDMS — form primitives, faithfully ported from the prototype's
   ds/{Input,Select,Checkbox}.jsx and ds/Avatar.jsx (window DS).
   Inline styles kept verbatim. */
import {
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useEffect,
  useId,
  useRef,
} from 'react'

type Size = 'sm' | 'md' | 'lg'

// ---- Input --------------------------------------------------------------
export function Input({
  label,
  hint,
  error,
  leading = null,
  trailing = null,
  size = 'md',
  mono = false,
  block = true,
  id,
  style = {},
  ...rest
}: {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  leading?: ReactNode
  trailing?: ReactNode
  size?: Size
  mono?: boolean
  block?: boolean
  style?: CSSProperties
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'style'>) {
  const reactId = useId()
  const uid = id || reactId
  const h = size === 'sm' ? 'var(--control-sm)' : size === 'lg' ? 'var(--control-lg)' : 'var(--control-md)'
  return (
    <div style={{ display: block ? 'block' : 'inline-block', width: block ? '100%' : undefined }}>
      {label ? (
        <label
          htmlFor={uid}
          style={{
            display: 'block',
            marginBottom: 'var(--space-3)',
            font: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-medium)',
            color: 'var(--text-body)',
          }}
        >
          {label}
        </label>
      ) : null}
      <div
        className="lav-input-wrap"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          height: h,
          padding: '0 var(--space-5)',
          background: 'var(--surface-card)',
          border: `var(--border-hairline) solid ${error ? 'var(--danger-ink)' : 'var(--border-strong)'}`,
          borderRadius: 'var(--radius-0)',
          transition:
            'border-color var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)',
          ...style,
        }}
      >
        {leading ? <span style={{ display: 'inline-flex', color: 'var(--text-subtle)', flex: '0 0 auto' }}>{leading}</span> : null}
        <input
          id={uid}
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            border: 0,
            outline: 'none',
            background: 'transparent',
            color: 'var(--text-strong)',
            font: mono ? 'var(--font-mono)' : 'var(--font-ui)',
            fontSize: mono ? 'var(--text-sm)' : 'var(--text-md)',
          }}
          {...rest}
        />
        {trailing ? <span style={{ display: 'inline-flex', color: 'var(--text-subtle)', flex: '0 0 auto' }}>{trailing}</span> : null}
      </div>
      {error ? (
        <div style={{ marginTop: 'var(--space-2)', font: 'var(--font-caption)', color: 'var(--danger-ink)' }}>{error}</div>
      ) : hint ? (
        <div style={{ marginTop: 'var(--space-2)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{hint}</div>
      ) : null}
      <style>{`
        .lav-input-wrap:focus-within { border-color: var(--accent) !important; box-shadow: 0 0 0 2px var(--accent-soft); }
      `}</style>
    </div>
  )
}

// ---- Select -------------------------------------------------------------
export function Select({
  label,
  hint,
  error,
  size = 'md',
  block = true,
  id,
  children,
  style = {},
  ...rest
}: {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  size?: Size
  block?: boolean
  children?: ReactNode
  style?: CSSProperties
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'style'>) {
  const reactId = useId()
  const uid = id || reactId
  const h = size === 'sm' ? 'var(--control-sm)' : size === 'lg' ? 'var(--control-lg)' : 'var(--control-md)'
  return (
    <div style={{ display: block ? 'block' : 'inline-block', width: block ? '100%' : undefined }}>
      {label ? (
        <label
          htmlFor={uid}
          style={{
            display: 'block',
            marginBottom: 'var(--space-3)',
            font: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-medium)',
            color: 'var(--text-body)',
          }}
        >
          {label}
        </label>
      ) : null}
      <div
        className="lav-select-wrap"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          height: h,
          background: rest.disabled ? 'var(--surface-sunken)' : 'var(--surface-card)',
          border: `var(--border-hairline) solid ${error ? 'var(--danger-ink)' : rest.disabled ? 'var(--border-default)' : 'var(--border-strong)'}`,
          borderRadius: 'var(--radius-0)',
          transition:
            'border-color var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)',
          ...style,
        }}
      >
        <select
          id={uid}
          style={{
            appearance: 'none',
            WebkitAppearance: 'none',
            flex: 1,
            /* Иначе поле распирает самый длинный вариант: у элемента flex
               min-width: auto — по содержимому. Выбор реестра на обзоре проекта
               растягивался с заданных 260 до 530 и вылезал за край окна на
               220 px; спасал только overflow: hidden у карточки. */
            minWidth: 0,
            height: '100%',
            padding: '0 30px 0 var(--space-5)',
            border: 0,
            outline: 'none',
            background: 'transparent',
            color: rest.disabled ? 'var(--text-subtle)' : 'var(--text-strong)',
            font: 'var(--font-ui)',
            fontSize: 'var(--text-md)',
            cursor: rest.disabled ? 'not-allowed' : 'pointer',
          }}
          {...rest}
        >
          {children}
        </select>
        {/* У заблокированного поля стрелки нет: она обещает выпадающий список,
            которого не будет, — поле читалось бы как сломанное. */}
        {rest.disabled ? null : (
          <span
            style={{
              position: 'absolute',
              right: 'var(--space-5)',
              pointerEvents: 'none',
              color: 'var(--text-subtle)',
              fontSize: '10px',
            }}
          >
            {'▼'}
          </span>
        )}
      </div>
      {error ? (
        <div style={{ marginTop: 'var(--space-2)', font: 'var(--font-caption)', color: 'var(--danger-ink)' }}>{error}</div>
      ) : hint ? (
        <div style={{ marginTop: 'var(--space-2)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{hint}</div>
      ) : null}
      <style>{`
        .lav-select-wrap:focus-within { border-color: var(--accent) !important; box-shadow: 0 0 0 2px var(--accent-soft); }
      `}</style>
    </div>
  )
}

// ---- Checkbox -----------------------------------------------------------
export function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
  id,
  style = {},
}: {
  checked?: boolean
  indeterminate?: boolean
  onChange?: InputHTMLAttributes<HTMLInputElement>['onChange']
  label?: ReactNode
  disabled?: boolean
  id?: string
  style?: CSSProperties
}) {
  const ref = useRef<HTMLInputElement>(null)
  const reactId = useId()
  const uid = id || reactId
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked
  }, [indeterminate, checked])
  const box = (
    <span
      style={{
        position: 'relative',
        display: 'inline-grid',
        placeItems: 'center',
        width: 16,
        height: 16,
        flex: '0 0 auto',
        background: checked || indeterminate ? 'var(--accent)' : 'var(--surface-card)',
        border: `var(--border-hairline) solid ${checked || indeterminate ? 'var(--accent)' : 'var(--border-strong)'}`,
        borderRadius: 'var(--radius-sm)',
        color: 'var(--on-accent)',
        transition:
          'background var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard)',
      }}
    >
      {checked ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : indeterminate ? (
        <span style={{ width: 8, height: 2, background: 'var(--on-accent)' }} />
      ) : null}
    </span>
  )
  return (
    <label
      htmlFor={uid}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      className="lav-cb"
    >
      {/* Обводка фокуса рисуется на квадрате рядом: сам input скрыт нулевым
          размером, и общее правило :focus-visible приземлялось на него — то
          есть в никуда. Правило одно на приложение (ui.css, `.lav-cb`): флажок
          есть в каждой строке реестра, и стиль на экземпляр означал бы двести
          таблиц стилей на странице. */}
      <input
        ref={ref}
        id={uid}
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={onChange}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      {box}
      {label != null ? (
        <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-md)', color: 'var(--text-body)' }}>{label}</span>
      ) : null}
    </label>
  )
}


// ---- Avatar -------------------------------------------------------------
const TINTS: [string, string][] = [
  ['--blue-50', '--blue-600'],
  ['--green-50', '--green-600'],
  ['--amber-50', '--amber-600'],
  ['--violet-50', '--violet-600'],
  ['--red-50', '--red-600'],
]
function hash(str = ''): number {
  let h = 0
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0
  return Math.abs(h)
}
export function Avatar({
  name = '',
  src = null,
  size = 28,
  square = true,
  style = {},
}: {
  name?: string
  src?: string | null
  size?: number
  square?: boolean
  style?: CSSProperties
}) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('')
  const tint = TINTS[hash(name) % TINTS.length]!
  const [bg, fg] = tint
  const radius = square ? 'var(--radius-sm)' : 'var(--radius-pill)'
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: radius,
          border: 'var(--border-hairline) solid var(--border-default)',
          ...style,
        }}
      />
    )
  }
  return (
    <span
      title={name}
      style={{
        display: 'inline-grid',
        placeItems: 'center',
        width: size,
        height: size,
        flex: '0 0 auto',
        background: `var(${bg})`,
        color: `var(${fg})`,
        border: 'var(--border-hairline) solid var(--border-default)',
        borderRadius: radius,
        font: 'var(--font-sans)',
        fontSize: Math.round(size * 0.4),
        fontWeight: 'var(--weight-semibold)',
        lineHeight: 1,
        userSelect: 'none',
        ...style,
      }}
    >
      {initials || '?'}
    </span>
  )
}
